import { useEffect, useRef } from 'react'
import { useWorld } from './use-world'

// Registers all solid tiles in the world sprite map so useCollision works,
// and sets the container dimensions to the full dungeon bounds.
// This replaces the HTML <Tile> components in Map.js for the geo-projected view.
export const useTileRegistry = (tiles, gridSize, entities = []) => {
  const { addSprite, removeSprite, setContainer } = useWorld()
  const prevTilesRef = useRef(null)

  useEffect(() => {
    if (!tiles?.length) return

    // Build a set of grid cells occupied by ladder entities so we can leave those
    // tiles passable even if the underlying tile data marks them solid.
    const ladderCells = new Set(
      entities
        .filter(e => e.entityType === 'ladder')
        .map(e => `${Math.floor(e.y / gridSize)}_${Math.floor(e.x / gridSize)}`)
    )

    // Clear sprites from the previous tile layout (handles level transitions where
    // the new layout has different dimensions from the old one).
    const prevTiles = prevTilesRef.current
    if (prevTiles) {
      prevTiles.forEach((row, r) => {
        row.forEach((_, c) => removeSprite({ id: `tile_${r}_${c}` }))
      })
    }
    prevTilesRef.current = tiles

    // Set container to full dungeon dimensions so isInViewport covers the whole dungeon
    const dungeonW = (tiles[0]?.length || 0) * gridSize
    const dungeonH = tiles.length * gridSize
    setContainer({ width: dungeonW, height: dungeonH })

    // Register each solid tile as a sprite for collision detection.
    // Skip tiles occupied by a ladder — the ladder entity overrides solidity there.
    tiles.forEach((row, r) => {
      row.forEach((tile, c) => {
        if (!tile?.solid) return
        if (ladderCells.has(`${r}_${c}`)) return
        addSprite({
          id: `tile_${r}_${c}`,
          x: c * gridSize,
          y: r * gridSize,
          width: gridSize,
          height: gridSize,
          config: { solid: true },
        })
      })
    })
  }, [tiles, gridSize])
}
